import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Control, Controller, FieldValues } from 'react-hook-form'
import moment from 'moment'
import { DateRangeField } from '@/components/molecules/date-range-field'
import { BULK_OPERATION_MAX_MONTHS, BulkOperationPeriodType, countBulkOperationPeriodDays } from '@/models/room-manager/bulk-operation'
import { BulkOperationField } from './bulk-operation-field'
import dayjs from 'dayjs'

// 期間は開始日・終了日を同時に変更するため、2値をまとめて1フィールドとして登録する
interface FormValue extends FieldValues {
  period: BulkOperationPeriodType
}

type BulkOperationPeriodFieldProps<T extends FormValue> = {
  // any は react-hook-form の TContext 既定値。InputSalesSubject など既存の共通フィールドと同じ書き方
  control: T extends FormValue ? Control<T, any> : never
  disabled: boolean
}

export const BulkOperationPeriodField = <T extends FormValue>({ control, disabled }: BulkOperationPeriodFieldProps<T>) => {
  const { t } = useTranslation()

  return (
    <BulkOperationField label={t('roomManagerBulk.TargetPeriod')} required>
      <Controller
        control={control}
        name="period"
        // 上限超過はカレンダー側で選択不可にしているため、ここでは未確定の範囲だけを弾く
        rules={{ validate: (value: BulkOperationPeriodType) => Boolean(value.beginDate && value.endDate) }}
        render={({ field: { onChange, value } }) => {
          const dayCount = countBulkOperationPeriodDays(value)
          return (
            <>
              <div css={[periodRowStyle, disabled && lockedStyle]}>
                <DateRangeField
                  beginDate={value.beginDate}
                  endDate={value.endDate}
                  changeRangeDate={(beginDate, endDate) => onChange({ beginDate, endDate })}
                  // プリセットは下の選択不可日を通らず過去日や上限超過の範囲を確定させてしまうため出さない
                  hidePresets
                  // 過去日は開始日・終了日とも選べないようにする。上限日は終了日の選択時のみ制限する。
                  // 開始日を選び直すと DateRangeField 側で終了日がクリアされるため、上限は常に確定した開始日基準になる
                  isOutsideRange={(day, focusedInput) =>
                    day.isBefore(moment(), 'day') ||
                    (focusedInput === 'endDate' &&
                      Boolean(value.beginDate) &&
                      day.isAfter(
                        moment(dayjs(value.beginDate).add(BULK_OPERATION_MAX_MONTHS, 'month').subtract(1, 'day').format('YYYY-MM-DD')),
                        'day',
                      ))
                  }
                />
                {dayCount > 0 && <span className="day-count">{t('roomManagerBulk.DayCount', { days: dayCount })}</span>}
              </div>
              <p css={hintStyle}>{t('roomManagerBulk.PeriodLimitHint', { months: BULK_OPERATION_MAX_MONTHS })}</p>
            </>
          )
        }}
      />
    </BulkOperationField>
  )
}

const periodRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  '.day-count': {
    fontSize: 12,
    letterSpacing: 0.6,
    color: '#a3a3a3',
  },
})

// 実行中は日付選択を受け付けない。カレンダーが開いたままにならないよう入力全体を無効化する
const lockedStyle = css({
  pointerEvents: 'none',
  opacity: 0.6,
})

const hintStyle = css({
  marginTop: 8,
  fontSize: 11,
  lineHeight: 1.6,
  color: '#a3a3a3',
})
